// src/contexts/SocketContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { ISocketMsg } from './types';
import { getConfig } from '../../config/runtimeConfig';
import * as PATH from '../Contants';

const SOCKET_HOST = 'wss://sc0-sso.nblib.com';
const SESSION_STORAGE_KEY = 'suep_reader_session_id';

interface SocketContextType {
  socketMsg: ISocketMsg | undefined;
  setDetailPage: (data: string) => void;
  setBookPage: (data: string) => void;
  setCloseBook: (data: string) => void;
  sessionId: string;
  memberId?: number;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

const getSessionId = () => {
  const storedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (storedSessionId) {
    return storedSessionId;
  }
  const newSessionId = crypto.randomUUID();
  localStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
  return newSessionId;
};

const getMemberIdFromStorage = (): number | undefined => {
  try {
    const raw = localStorage.getItem(PATH.USER_INFO);
    if (!raw) {
      return undefined;
    }
    const obj = JSON.parse(raw);
    const id = obj?.member?.memberId;
    if (id === undefined || id === null || id === '') {
      return undefined;
    }
    const n = Number(id);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
};

const getAccessToken = (): string | undefined => {
  return localStorage.getItem(PATH.ACCESS_TOKEN) || undefined;
};

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const [socketMsg, setSocketMsg] = useState<ISocketMsg>();
  const [sessionId] = useState(getSessionId);
  const [memberId, setMemberId] = useState<number | undefined>(
    getMemberIdFromStorage
  );
  const [needRetry, setNeedRetry] = useState(true);

  const [detailPage, setDetailPage] = useState('');
  const [bookPage, setBookPage] = useState('');
  const [closeBook, setCloseBook] = useState('');

  useEffect(() => {
    const syncMember = () => setMemberId(getMemberIdFromStorage());
    syncMember();
    window.addEventListener('storage', syncMember);
    return () => window.removeEventListener('storage', syncMember);
  }, []);

  useEffect(() => {
    if (!needRetry) {
      return;
    }

    const config = getConfig();
    const orgId = config.WEB_NAME;
    if (!orgId) {
      return;
    }

    const currentMemberId = getMemberIdFromStorage();
    const token = getAccessToken();
    const query: Record<string, string> = { orgId };
    if (currentMemberId != null) {
      query.memberId = String(currentMemberId);
    }

    const newSocket = io(`${SOCKET_HOST}?orgId=${orgId}`, {
      query,
      auth: {
        token,
        memberId: currentMemberId,
      },
    });
    setSocket(newSocket);
    setMemberId(currentMemberId);

    newSocket.on('sendMsg', (message: ISocketMsg) => {
      setSocketMsg(Object.assign(message, { socketId: newSocket.id }));
    });

    return () => {
      newSocket.disconnect();
      newSocket.off();
      setSocket(null);
    };
  }, [needRetry]);

  // Fatal connection errors only — do not treat open-book conflict as fatal
  useEffect(() => {
    if (!socketMsg?.error) {
      return;
    }
    if (socketMsg.conflict || socketMsg.canOpen === false) {
      return;
    }
    socket?.disconnect();
    socket?.off();
    setNeedRetry(false);
  }, [socketMsg, socket]);

  useEffect(() => {
    if (socket !== null && detailPage) {
      const messageObj = JSON.parse(detailPage);
      socket.emit(
        'detailPage',
        JSON.stringify({
          ...messageObj,
          sessionId,
          memberId: getMemberIdFromStorage(),
        })
      );
    }
  }, [detailPage, socket, sessionId]);

  useEffect(() => {
    if (socket !== null && bookPage) {
      const messageObj = JSON.parse(bookPage);
      const mid = getMemberIdFromStorage();
      socket.emit(
        'bookPage',
        JSON.stringify({
          ...messageObj,
          sessionId,
          memberId: messageObj.memberId ?? mid,
        })
      );
    }
  }, [bookPage, socket, sessionId]);

  useEffect(() => {
    if (socket !== null && closeBook) {
      const messageObj = JSON.parse(closeBook);
      const mid = getMemberIdFromStorage();
      socket.emit(
        'closeBook',
        JSON.stringify({
          ...messageObj,
          sessionId,
          memberId: messageObj.memberId ?? mid,
        })
      );
      setCloseBook('');
    }
  }, [closeBook, socket, sessionId]);

  return (
    <SocketContext.Provider
      value={{
        socketMsg,
        setDetailPage,
        setBookPage,
        setCloseBook,
        sessionId,
        memberId,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
